// The Task PERMANENT-DELETE control — footer-anchored in the Task-detail modal (owner ruling 2026-07-24:
// the board card stays a pure Signal; deleting is a considered act taken from the thread, two-step
// confirmed, never a card affordance and never silent). The owning Tasks Gate still re-checks human
// authority on the route; a refusal renders its note honestly in place. A successful delete reconciles
// the Task projections, toasts, and closes the thread — the Task no longer exists to render.

import { useState } from 'react';
import { Icon } from './Icon.tsx';
import { useShell } from './ShellContext.tsx';
import { useSurfaceApi } from '../surfaces/SurfaceApiContext.tsx';
import { notifyTaskProjectionsChanged } from '../surfaces/task-projection-events.ts';

/** The two-step confirmation strip the delete control expands into. */
function DeleteTaskConfirmation({
  title,
  busy,
  onCancel,
  onConfirm,
}: {
  readonly title: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  return (
    <div className="task-delete-confirm" role="group" aria-label={`Delete task: ${title}`}>
      <span>This permanently deletes the task.</span>
      <button type="button" className="task-delete-cancel" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      <button
        type="button"
        className="task-delete-confirm-button"
        disabled={busy}
        onClick={onConfirm}
      >
        {busy ? 'Deleting…' : 'Confirm delete'}
      </button>
    </div>
  );
}

/** Delete-control state: arm → explicit confirm routes the delete; refusal surfaces its note in place. */
function useTaskDelete(
  taskId: string,
  onDeleted: () => void,
): {
  readonly confirming: boolean;
  readonly setConfirming: (next: boolean) => void;
  readonly busy: boolean;
  readonly note: string | null;
  readonly deleteTask: () => void;
} {
  const api = useSurfaceApi();
  const shell = useShell();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const deleteTask = (): void => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    void api
      .deleteTask(taskId)
      .then((result) => {
        if (!result.ok) {
          setNote(result.note ?? 'The task could not be deleted.');
          return;
        }
        shell.showToast('Task permanently deleted.');
        notifyTaskProjectionsChanged();
        onDeleted();
      })
      .catch(() => setNote('The task could not be deleted.'))
      .finally(() => setBusy(false));
  };
  return { confirming, setConfirming, busy, note, deleteTask };
}

/** Explicit two-step permanent delete, footer-anchored (owner ruling 2026-07-24). */
export function TaskDeleteControl({
  taskId,
  title,
  onDeleted,
}: {
  readonly taskId: string;
  readonly title: string;
  readonly onDeleted: () => void;
}): React.JSX.Element {
  const control = useTaskDelete(taskId, onDeleted);
  return (
    <div className="task-delete-control">
      {control.confirming ? (
        <DeleteTaskConfirmation
          title={title}
          busy={control.busy}
          onCancel={() => control.setConfirming(false)}
          onConfirm={control.deleteTask}
        />
      ) : (
        <button
          type="button"
          className="task-delete"
          aria-label={`Delete task: ${title}`}
          onClick={() => control.setConfirming(true)}
        >
          <Icon name="trash" size={12} /> Delete task
        </button>
      )}
      {control.note !== null ? (
        <p className="task-delete-note" role="status">
          {control.note}
        </p>
      ) : null}
    </div>
  );
}
